import { jsonld } from '@nexa/seo';
import type { PageId } from '@nexa/content';
import { SITE } from '@/core/config/site.ts';
import { useCopy } from '@/core/i18n';
import { JsonLd, type JsonLdData } from './JsonLd.tsx';
import { PageHead } from './PageHead.tsx';
import { useBreadcrumb } from './useBreadcrumb.ts';

export interface PageMetaProps {
  readonly pageId: PageId;
  /** Ảnh OG riêng của trang. Bỏ trống ⇒ dùng ảnh mặc định của site. */
  readonly image?: string;
  /** Schema bổ sung của riêng trang này (Organization, Product, FAQPage…). */
  readonly jsonLd?: readonly (JsonLdData | undefined)[];
}

/**
 * MỘT dòng lo trọn phần SEO của một trang: title/description lấy từ `copy.seo[pageId]`,
 * canonical + hreflang + OG + Twitter do `PageHead` sinh, breadcrumb tự dựng theo pageId.
 *
 * Mọi trang chỉ cần `<PageMeta pageId="..." />`. Gom lại như vậy vì thiếu canonical hay thiếu
 * hreflang là loại lỗi KHÔNG nhìn thấy khi xem trang — chỉ thấy sau vài tuần khi thứ hạng tụt.
 * Không để mỗi trang tự khai từng thẻ thì không trang nào thiếu được.
 */
export function PageMeta({ pageId, image, jsonLd = [] }: PageMetaProps) {
  const copy = useCopy();
  const crumbs = useBreadcrumb(pageId);
  const seo = copy.seo[pageId];

  return (
    <>
      <PageHead
        pageId={pageId}
        title={seo.title}
        description={seo.description}
        {...(image === undefined ? {} : { image })}
      />
      <JsonLd data={[jsonld.breadcrumbList(SITE, crumbs), ...jsonLd]} />
    </>
  );
}
